import { makeQueryOptions } from '@mint-lib/api-zod-support/react-query';

import { defaultApi, defaultClient } from '@mint-sdk/api-core';
import { z } from 'zod';

import {
  ActivityResponseSchema,
  ActivitySchema,
  BudgetingLevel,
  BudgetingLevelResponseSchema,
  ChannelNamesQuerySchema,
  ChannelNamesResponseSchema,
  CreateActivityRequestSchema,
  CreateActivityResponseSchema,
  MediaRowsQuerySchema,
  MediaRowsResponseSchema,
  PlatformsResponseSchema,
  previewActivityListResponse,
} from '../shared/schemas/activity.js';

export type CreateActivityResponse = z.infer<
  typeof CreateActivityResponseSchema
>;

export type PreviewActivityList = z.infer<typeof previewActivityListResponse>;

export type BudgetingLevel = z.infer<typeof BudgetingLevel>;
export type Activity = z.infer<typeof ActivitySchema>;

export const createActivity = defaultApi.declareEndpoint({
  url: 'planning-service/activities/',
  method: 'POST',
  requestSchema: CreateActivityRequestSchema,
  responseSchema: CreateActivityResponseSchema,
});

export const useCreateActivity = defaultClient.mutation({
  url: 'planning-service/activities/',
  method: 'POST',
  requestSchema: CreateActivityRequestSchema,
  responseSchema: CreateActivityResponseSchema,
});

const previewActivityList = defaultApi.declareEndpoint({
  url: 'planning-service/activities/preview/' as const,
  method: 'GET',
  querySchema: z.object({
    mediaPlanId: z.string(),
    withConfiguredMediaRows: z.boolean().optional(),
  }),
  responseSchema: previewActivityListResponse,
  clientOptions: {
    request: {
      snakeCaseQueryParams: true,
    },
  },
});

export const getPreviewActivityListQueryOptions = makeQueryOptions(
  previewActivityList,
  {
    processResponse(data) {
      return data;
    },
  },
);

export const getActivityById = defaultClient.query({
  url: 'planning-service/activities/$activityId/' as const,
  method: 'GET',
  responseSchema: ActivityResponseSchema,
  processResponse(data) {
    return data.data;
  },
  querySchema: z.object({
    mediaPlanId: z.string(),
  }),
  clientOptions: {
    request: {
      snakeCaseQueryParams: true,
    },
  },
});

export const getBudgetingLevelsList = defaultClient.query({
  url: 'planning-service/activities/$activityId/budgeting-levels/' as const,
  method: 'GET',
  responseSchema: BudgetingLevelResponseSchema,
  processResponse(data) {
    return data;
  },
});

export const getActivityPlatformsQueryOptions = defaultClient.query({
  url: 'planning-service/data-ingestion/activities/$activityId/platforms/' as const,
  method: 'GET',
  querySchema: z.object({
    onlyVirtual: z.boolean().optional(),
    withConfiguredMediaRows: z.boolean().optional(),
  }),
  responseSchema: PlatformsResponseSchema,
  processResponse(data) {
    return data.data;
  },
  clientOptions: {
    request: {
      snakeCaseQueryParams: true,
    },
  },
});

export const getActivityChannelNamesQueryOptions = defaultClient.query({
  url: 'planning-service/data-ingestion/activities/$activityId/channel-names/' as const,
  method: 'GET',
  querySchema: ChannelNamesQuerySchema,
  responseSchema: ChannelNamesResponseSchema,
  processResponse(data) {
    return data.data?.items;
  },
  clientOptions: {
    request: {
      snakeCaseQueryParams: true,
    },
  },
});

export const getActivityMediaRowsQueryOptions = defaultClient.query({
  url: 'planning-service/data-ingestion/activities/$activityId/media-rows/' as const,
  method: 'GET',
  querySchema: MediaRowsQuerySchema,
  responseSchema: MediaRowsResponseSchema,
  processResponse(data) {
    return data.data?.items;
  },
  clientOptions: {
    request: {
      snakeCaseQueryParams: true,
    },
  },
});

export const getActivityImportTemplate = defaultApi.declareStream({
  url: 'planning-service/activities/$activityId/import/xlsx/template/' as const,
  method: 'GET',
});

export const importActivity = defaultApi.declareMultipart({
  url: 'planning-service/activities/$activityId/import/xlsx/' as const,
  method: 'POST',
  requestSchema: z.object({
    file: z.instanceof(File),
  }),
  responseSchema: z.union([z.string(), z.object({ status: z.string() })]),
});
